import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { adminAPI } from '../../api/client'
import AdminEmptyState from './AdminEmptyState'
import type { AdminImeiAnalytics, AdminImeiCheckRow, AdminImeiSummary } from './adminTypes'
import { exportCsv, formatWhen, sparklinePath } from './adminUtils'

type Props = {
  token: string
}

export default function AdminImeiPanel({ token }: Props) {
  const { t, i18n } = useTranslation()
  const [summary, setSummary] = useState<AdminImeiSummary | null>(null)
  const [analytics, setAnalytics] = useState<AdminImeiAnalytics | null>(null)
  const [items, setItems] = useState<AdminImeiCheckRow[]>([])
  const [q, setQ] = useState('')
  const [service, setService] = useState('')
  const [stolenOnly, setStolenOnly] = useState(false)
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const load = useCallback(() => {
    setLoading(true)
    setError('')
    Promise.all([
      adminAPI.imeiSummary(token),
      adminAPI.imeiAnalytics(token, { days: 30 }),
      adminAPI.imeiChecks(token, {
        q: q.trim() || undefined,
        service: service.trim() || undefined,
        stolen_only: stolenOnly || undefined,
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
        limit: 100,
      }),
    ])
      .then(([sumRes, analyticsRes, listRes]) => {
        setSummary(sumRes.data as AdminImeiSummary)
        setAnalytics(analyticsRes.data as AdminImeiAnalytics)
        setItems((listRes.data?.items as AdminImeiCheckRow[]) ?? [])
      })
      .catch((err) => setError(err.response?.data?.detail || err.message))
      .finally(() => setLoading(false))
  }, [token, q, service, stolenOnly, dateFrom, dateTo])

  useEffect(() => {
    const tmr = window.setTimeout(load, q ? 250 : 0)
    return () => window.clearTimeout(tmr)
  }, [load, q])

  function exportRows() {
    exportCsv(
      'imei-checks.csv',
      ['when', 'email', 'service', 'imei', 'device', 'valid', 'stolen'],
      items.map((row) => [
        row.created_at ?? '',
        row.user_email ?? '',
        row.service,
        row.imei_masked,
        `${row.brand ?? ''} ${row.model ?? ''}`.trim(),
        row.is_valid ? 'yes' : 'no',
        row.is_stolen ? 'yes' : 'no',
      ]),
    )
  }

  const chartPath = analytics ? sparklinePath(analytics.daily_checks, 640, 120) : ''

  return (
    <div className="vc-admin-panel vc-admin-panel--card">
      <header className="vc-admin-panel__head">
        <div>
          <h2>{t('admin.nav.imei')}</h2>
          <p className="vc-admin-panel__hint">{t('admin.imeiIntro')}</p>
        </div>
        <div className="vc-admin-panel__actions">
          <button type="button" className="vc-admin-btn vc-admin-btn--ghost" onClick={exportRows}>
            {t('admin.exportCsv')}
          </button>
          <button type="button" className="vc-admin-btn vc-admin-btn--ghost" onClick={load}>
            {loading ? '…' : t('catalog.refresh')}
          </button>
        </div>
      </header>

      {error ? <p className="vc-admin-error">{error}</p> : null}

      {summary ? (
        <>
          <div className="vc-admin-stats vc-admin-stats--3">
            <div className="vc-admin-stat">
              <span className="vc-admin-stat__value">{summary.platform_total_checks}</span>
              <span className="vc-admin-stat__label">{t('admin.imeiPlatformTotal')}</span>
            </div>
            <div className="vc-admin-stat">
              <span className="vc-admin-stat__value">{summary.saved_reports_total}</span>
              <span className="vc-admin-stat__label">{t('admin.imeiSavedReports')}</span>
            </div>
            <div className="vc-admin-stat">
              <span className="vc-admin-stat__value">{summary.unique_users}</span>
              <span className="vc-admin-stat__label">{t('admin.imeiUniqueUsers')}</span>
            </div>
            <div className="vc-admin-stat vc-admin-stat--warn">
              <span className="vc-admin-stat__value">{summary.stolen_today}</span>
              <span className="vc-admin-stat__label">{t('admin.imeiStolenToday')}</span>
            </div>
            <div className="vc-admin-stat">
              <span className="vc-admin-stat__value">{summary.locked_today}</span>
              <span className="vc-admin-stat__label">{t('admin.imeiLockedToday')}</span>
            </div>
            <div className="vc-admin-stat">
              <span className="vc-admin-stat__value">{summary.financed_today}</span>
              <span className="vc-admin-stat__label">{t('admin.imeiFinancedToday')}</span>
            </div>
          </div>

          {analytics ? (
            <section className="vc-admin-card">
              <h3>{t('admin.imeiDailyChart')}</h3>
              {chartPath ? (
                <svg className="vc-admin-line-chart" viewBox="0 0 640 120" aria-hidden="true">
                  <path d={chartPath} fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
                </svg>
              ) : null}
              <div className="vc-admin-inline-stats">
                <span>
                  {t('admin.imeiRiskStolen')}: <strong>{analytics.risk.stolen_pct}%</strong>
                </span>
                <span>
                  {t('admin.imeiRiskInvalid')}: <strong>{analytics.risk.invalid_pct}%</strong>
                </span>
              </div>
              {analytics.top_models.length > 0 ? (
                <div className="vc-admin-table-wrap">
                  <table className="vc-admin-table">
                    <thead>
                      <tr>
                        <th>{t('admin.imeiDevice')}</th>
                        <th>{t('admin.imeiChecksCount')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {analytics.top_models.map((row) => (
                        <tr key={row.model}>
                          <td>{row.model}</td>
                          <td>{row.checks_count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : null}
            </section>
          ) : null}

          {summary.top_users.length > 0 ? (
            <section className="vc-admin-card vc-admin-card--compact">
              <h3>{t('admin.imeiTopUsers')}</h3>
              <div className="vc-admin-table-wrap">
                <table className="vc-admin-table">
                  <thead>
                    <tr>
                      <th>{t('auth.email')}</th>
                      <th>{t('admin.imeiChecksCount')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {summary.top_users.map((user) => (
                      <tr key={user.user_id}>
                        <td>
                          <strong>{user.full_name || user.email}</strong>
                          {user.full_name ? <div className="vc-admin-muted">{user.email}</div> : null}
                        </td>
                        <td>{user.checks_count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          ) : null}
        </>
      ) : null}

      <h3 className="vc-admin-subheading">{t('admin.imeiRecentChecks')}</h3>
      <div className="vc-admin-filters">
        <input
          type="search"
          className="vc-admin-input vc-admin-input--search"
          placeholder={t('admin.imeiSearch')}
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <input
          type="text"
          className="vc-admin-input"
          placeholder={t('admin.imeiService')}
          value={service}
          onChange={(e) => setService(e.target.value)}
        />
        <label className="vc-admin-check">
          <input type="checkbox" checked={stolenOnly} onChange={(e) => setStolenOnly(e.target.checked)} />
          {t('admin.imeiStolenOnly')}
        </label>
        <input type="date" className="vc-admin-input" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
        <input type="date" className="vc-admin-input" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
      </div>

      {loading && items.length === 0 ? <p className="meta-text">{t('common.loading')}</p> : null}
      {!loading && items.length === 0 ? (
        <AdminEmptyState title={t('admin.imeiNoChecks')} hint={t('admin.imeiIntro')} />
      ) : null}

      {items.length > 0 ? (
        <div className="vc-admin-table-wrap">
          <table className="vc-admin-table vc-admin-table--clickable">
            <thead>
              <tr>
                <th>{t('admin.imeiWhen')}</th>
                <th>{t('auth.email')}</th>
                <th>{t('admin.imeiService')}</th>
                <th>IMEI</th>
                <th>{t('admin.imeiDevice')}</th>
                <th>{t('admin.status')}</th>
              </tr>
            </thead>
            <tbody>
              {items.map((row) => (
                <tr key={row.id}>
                  <td>{formatWhen(row.created_at, i18n.language)}</td>
                  <td>
                    <strong>{row.user_full_name || row.user_email}</strong>
                    {row.user_full_name ? <div className="vc-admin-muted">{row.user_email}</div> : null}
                  </td>
                  <td>{row.service}</td>
                  <td>
                    <code className="vc-admin-code">{row.imei_masked}</code>
                  </td>
                  <td>
                    {row.brand || row.model ? (
                      <>
                        {row.brand} {row.model}
                      </>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td>
                    <span className={`vc-admin-badge${row.is_valid ? '' : ' vc-admin-badge--warn'}`}>
                      {row.is_valid ? t('admin.imeiValid') : t('admin.imeiInvalid')}
                    </span>
                    {row.is_stolen ? (
                      <span className="vc-admin-badge vc-admin-badge--warn">{t('admin.imeiStolen')}</span>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  )
}
